import React, { useEffect, useState, useCallback } from 'react';
import * as api from '../../api';
import { RNLLoader } from '../UI';


// ─── Icons (currentColor so the parent drives the hue) ────────────────────────
const ic = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' };

const Icons = {
  takeOff:   () => <svg {...ic}><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  checkBox:  () => <svg {...ic}><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  clipboard: () => <svg {...ic}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>,
  store:     () => <svg {...ic}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  approve:   () => <svg {...ic}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  badge:     () => <svg {...ic}><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>,
  cart:      () => <svg {...ic}><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>,
  quote:     () => <svg {...ic}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  users:     () => <svg {...ic}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  truck:     () => <svg {...ic}><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
  delivery:  () => <svg {...ic}><path d="M5 17H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3"/><rect x="9" y="11" width="14" height="10" rx="2"/><circle cx="12" cy="21" r="1"/><circle cx="20" cy="21" r="1"/></svg>,
  list:      () => <svg {...ic}><path d="M21 10H7M21 6H3M21 14H3M21 18H7"/></svg>,
  file:      () => <svg {...ic}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>,
  box:       () => <svg {...ic}><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>,
  bars:      () => <svg {...ic}><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>,
  undo:      () => <svg {...ic}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/></svg>,
  xCircle:   () => <svg {...ic}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
};

// ─── Single stat card ─────────────────────────────────────────────────────────
function Card({ Icon, label, count, highlight, onClick, accent, bg }) {
  const hasCount = count != null && count > 0;
  const [hovered, setHovered] = React.useState(false);

  const cardAccent = accent || '#1e3a8a';
  const cardBg     = bg     || 'var(--bg-card)';

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: cardBg,
        border: highlight ? `2px solid ${cardAccent}` : `1px solid ${cardAccent}30`,
        borderTop: `3px solid ${cardAccent}`,
        borderRadius: 10,
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: hovered ? `0 8px 22px ${cardAccent}28` : 'var(--shadow-sm)',
        transform: hovered ? 'translateY(-3px) scale(1.02)' : 'translateY(0) scale(1)',
        transition: 'box-shadow .2s ease, transform .2s ease',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: cardAccent + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: cardAccent,
          flexShrink: 0,
        }}>
          <Icon />
        </div>
        <span style={{
          fontSize: 26,
          fontWeight: 700,
          lineHeight: 1,
          color: hasCount ? cardAccent : 'var(--text-secondary)',
        }}>
          {count ?? 0}
        </span>
      </div>
      <span style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.4, fontWeight: 500 }}>
        {label}
      </span>
    </div>
  );
}

// ─── Lifecycle stages bar ─────────────────────────────────────────────────────
const STAGES = [
  'Take-Off', 'Take-Off Approval', 'Material Request', 'Cost Control Review',
  'Store Review', 'Management Approval', 'Procurement Request', 'Quotation Comparison',
  'Supplier Selection', 'Purchase Order', 'GRN', 'Store Receipt', 'Site Issue',
];

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function MRDashboard({ onNewRequest, onLoadTakeoffs, onGoToCC, onGoToStore, onGoToMgmt, onGoToApproved }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getMRLifecycleStats();
      setStats(data);
    } catch {
      setStats({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = stats || {};

  const rows = [
    [
      { Icon: Icons.checkBox,  label: 'Approved Take-Offs Awaiting Material Request', key: 'approved_take_offs_awaiting_mr', onClick: onLoadTakeoffs, accent: '#2563eb', bg: '#eff6ff' },
      { Icon: Icons.clipboard, label: 'Material Requests Pending Cost Control',        key: 'pending_cc_review',              onClick: onGoToCC,       accent: '#d97706', bg: '#fffbeb' },
      { Icon: Icons.store,     label: 'Pending Store Review',                          key: 'pending_store_review',           onClick: onGoToStore,    accent: '#7c3aed', bg: '#faf5ff' },
      { Icon: Icons.approve,   label: 'Pending Management Approval',                   key: 'pending_mgmt_approval',          onClick: onGoToMgmt,     accent: '#4338ca', bg: '#eef2ff' },
      { Icon: Icons.badge,     label: 'Approved',                                      key: 'approved',                       onClick: onGoToApproved, accent: '#16a34a', bg: '#f0fdf4' },
      { Icon: Icons.cart,      label: 'Procurement Requests Open',                     key: 'procurement_requests_open',      onClick: null,           accent: '#0d9488', bg: '#f0fdfa' },
    ],
    [
      { Icon: Icons.quote,    label: 'Quotations Awaiting Comparison', key: 'quotations_awaiting_comparison', onClick: null, accent: '#0891b2', bg: '#ecfeff' },
      { Icon: Icons.users,    label: 'Supplier Selection Pending',     key: 'supplier_selection_pending',     onClick: null, accent: '#0284c7', bg: '#f0f9ff' },
      { Icon: Icons.truck,    label: 'Purchase Orders Open',           key: 'purchase_orders_open',           onClick: null, accent: '#65a30d', bg: '#f7fee7' },
      { Icon: Icons.delivery, label: 'PO Awaiting Delivery',          key: 'po_awaiting_delivery',           onClick: null, accent: '#ea580c', bg: '#fff7ed' },
      { Icon: Icons.delivery, label: 'Partial Deliveries',            key: 'partial_deliveries',             onClick: null, accent: '#e11d48', bg: '#fff1f2' },
      { Icon: Icons.list,     label: 'GRN Pending',                   key: 'grn_pending',                   onClick: null, accent: '#ca8a04', bg: '#fefce8' },
    ],
    [
      { Icon: Icons.file,    label: 'Store Receipt Pending',          key: 'store_receipt_pending',    onClick: null, accent: '#059669', bg: '#ecfdf5' },
      { Icon: Icons.box,     label: 'Materials Ready for Site Issue', key: 'materials_ready_for_issue',onClick: null, accent: '#16a34a', bg: '#f0fdf4' },
      { Icon: Icons.bars,    label: 'Variations Pending',             key: 'variations_pending',       onClick: null, accent: '#dc2626', bg: '#fef2f2' },
      { Icon: Icons.undo,    label: 'Returned Transactions',          key: 'returned_transactions',    onClick: null, accent: '#b45309', bg: '#fffbeb' },
      { Icon: Icons.xCircle, label: 'Rejected Transactions',          key: 'rejected_transactions',    onClick: null, accent: '#be123c', bg: '#fff1f2' },
    ],
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: 20, color: 'var(--text-primary)' }}>
            Material Lifecycle Dashboard
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            One operational control centre for the{' '}
            <span style={{ color: '#3b82f6', fontWeight: 500 }}>full material journey</span>
            {' '}— Engineering → Control → Procurement → Receipt → Site Issue.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={onNewRequest}
            style={{ background: '#0b318b', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            + New Request
          </button>
          <button
            onClick={onLoadTakeoffs}
            style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            ⇩ Load Approved Take-Offs
          </button>
        </div>
      </div>

      {/* Cards */}
      {loading ? (
        <RNLLoader title="Loading dashboard..." subtitle="Fetching lifecycle stats" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((row, ri) => (
            <div key={ri} style={{ display: 'grid', gridTemplateColumns: `repeat(${row.length}, 1fr)`, gap: 12 }}>
              {row.map(({ Icon, label, key, onClick, accent, bg }) => (
                <Card
                  key={key}
                  Icon={Icon}
                  label={label}
                  count={s[key]}
                  highlight={(s[key] || 0) > 0 && !!onClick}
                  onClick={onClick}
                  accent={accent}
                  bg={bg}
                />
              ))}
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
